/**
 * History import (ADR-001 §10.1; prototype "+ Add → Old tasks or old escalations", managers only). The pipeline is
 * the usage report upload's: sign → the browser PUTs the file to object storage → POST with dry_run parses in the
 * worker → GET is the per-row preview → POST with dry_run=false writes the ready rows. See history-import.ts.
 */
import { IdempotencyHeaders } from './common.js';
import { HistoryImport, HistoryImportDetail, HistoryImportIdParams, RunHistoryImportBody, SignHistoryImportBody, } from './history-import.js';
import { API_BASE, defineRoute } from './route.js';
import { SignUploadResponse } from './usage.js';
/** Managers: the founder and the associate director (the ops head). They assign work to anyone; members cannot. */
const MANAGERS = ['task.reassign'];
const NOTE = 'Managers (founder, associate director). Members and everyone else: 403.';
export const historyRoutes = {
    'historyImport.sign': defineRoute({
        id: 'historyImport.sign',
        method: 'POST',
        path: `${API_BASE}/tasks/history-import/sign`,
        summary: 'Get an upload slot for an old-tasks or old-escalations sheet (the browser sends the file directly)',
        description: '422 over 10 MB or on an extension other than .xlsx .csv .tsv .txt. The answer’s upload_id is the import id ' +
            'every later call takes. The original file is kept.' +
            ' 429 rate_limited (Postgres file store only) when the caller’s, or everyone’s, uploads not yet attached to anything are at their cap (FILES_PENDING_USER_MB / FILES_PENDING_TOTAL_MB), or the caller’s link uploads of the last 24 hours, attached or not, are at their day cap (FILES_UPLOAD_USER_DAY_MB); uploads nothing uses are deleted after 24 hours.',
        access: { auth: 'session', anyOf: MANAGERS, note: NOTE },
        headers: IdempotencyHeaders,
        body: SignHistoryImportBody,
        success: { status: 201, description: 'The upload slot', schema: SignUploadResponse },
        errors: ['no_session', 'forbidden', 'validation_failed', 'rate_limited', 'upstream_unavailable'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'historyImport.run': defineRoute({
        id: 'historyImport.run',
        method: 'POST',
        path: `${API_BASE}/tasks/history-import`,
        summary: 'Preview (dry_run, the default: parse in the worker) or apply an uploaded history sheet',
        description: 'dry_run=true: queues the parse and answers the import (status parsing; poll GET /tasks/history-import/{id} ' +
            'for the rows). Asking again while parsing, staged or applied answers the current state; on a failed read it ' +
            'reads the file again. 422 while the file has not arrived; 503 when the job queue is down. dry_run=false: writes every ready row in one transaction — tasks, or appraisal-record incidents ' +
            'and their escalations, all with source history; finished tasks with proof pre-checked; error rows are left ' +
            'out. 409 unless staged (so a sheet is never applied twice); 422 when no row is ready. Audited.',
        access: { auth: 'session', anyOf: MANAGERS, note: NOTE },
        headers: IdempotencyHeaders,
        body: RunHistoryImportBody,
        success: { status: 200, description: 'The import (written counts after apply)', schema: HistoryImport },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict', 'upstream_unavailable'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'historyImport.get': defineRoute({
        id: 'historyImport.get',
        method: 'GET',
        path: `${API_BASE}/tasks/history-import/{id}`,
        summary: 'The per-row preview: Ready, or why not (and, after apply, what each row wrote)',
        access: { auth: 'session', anyOf: MANAGERS, deny: 'forbidden', note: NOTE },
        params: HistoryImportIdParams,
        success: { status: 200, description: 'The import and its rows', schema: HistoryImportDetail },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
};
//# sourceMappingURL=routes.history.js.map